import { HttpErrorResponse, HttpEventType, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';

import { HttpNotificationService } from '../services/http-notification.service';

function isMutationMethod(method: string): boolean {
  return method === 'POST' || method === 'PUT' || method === 'DELETE';
}

function normalizePath(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname;
  } catch {
    return url;
  }
}

function buildSuccessMessage(method: string, path: string): string {
  if (path.includes('/submit')) {
    return 'Request submitted successfully.';
  }

  if (method === 'POST') {
    return 'Data saved successfully.';
  }

  if (method === 'DELETE') {
    return 'Item deleted successfully.';
  }

  return 'Changes updated successfully.';
}

function extractErrorTitle(error: HttpErrorResponse): string | null {
  const payload = error.error;

  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const titleCandidate = 'title' in payload ? (payload as { title?: unknown }).title : null;
  if (typeof titleCandidate !== 'string') {
    return null;
  }

  const title = titleCandidate.trim();
  return title || null;
}

function buildErrorMessage(error: HttpErrorResponse): string {
  const title = extractErrorTitle(error);
  const withTitle = (baseMessage: string): string => (title ? `${baseMessage} ${title}` : baseMessage);

  if (error.status === 404) {
    return withTitle('Resource not found (404).');
  }

  if (error.status === 400) {
    return withTitle('Request contains invalid data (400).');
  }

  if (error.status === 401 || error.status === 403) {
    return withTitle('Access denied. Please sign in again.');
  }

  if (error.status >= 500) {
    return withTitle('Server error. Please try again later.');
  }

  return withTitle('Request failed.');
}

export const requestNotificationInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isMutationMethod(req.method)) {
    return next(req);
  }

  const skipNotification = req.headers.has('X-Skip-Notification');
  const cleanReq = skipNotification ? req.clone({ headers: req.headers.delete('X-Skip-Notification') }) : req;

  const notifications = inject(HttpNotificationService);
  const requestPath = normalizePath(req.url);

  return next(cleanReq).pipe(
    tap(event => {
      if (!skipNotification && event.type === HttpEventType.Response) {
        notifications.showSuccess(buildSuccessMessage(req.method, requestPath));
      }
    }),
    catchError((error: HttpErrorResponse) => {
      if (!skipNotification) {
        notifications.showError(buildErrorMessage(error));
      }
      return throwError(() => error);
    }),
  );
};
